import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ProductsList from './ProductsList';
import ProductCategories from './ProductCategories';
import InventoryValuation from './InventoryValuation';
import ProductConfiguration from './ProductConfiguration';
import ProductTracking from './ProductTracking';
import ProductPriceLists from './ProductPriceLists';
import ProductSuppliers from './ProductSuppliers';
import ProductVariants from './ProductVariants';
import ProductStock from './ProductStock';
import StockMovements from './StockMovements';
import StockReservations from './StockReservations';
import ProductOverview from './ProductOverview';
import ProductRelatedOrders from './ProductRelatedOrders';
import ProductRelatedTransfers from './ProductRelatedTransfers';
import '../inventory.css';

const ProductManagement = ({ organization }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    // Load saved tab from localStorage
    const saved = localStorage.getItem(`productManagement_activeTab_${organization}`);
    return saved || 'overview';
  });

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (organization) {
      localStorage.setItem(`productManagement_activeTab_${organization}`, activeTab);
    }
  }, [activeTab, organization]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <ProductOverview organization={organization} />;
      case 'products':
        return <ProductsList organization={organization} />;
      case 'categories':
        return <ProductCategories organization={organization} />;
      case 'variants':
        return <ProductVariants organization={organization} />;
      case 'stock':
        return <ProductStock organization={organization} />;
      case 'movements':
        return <StockMovements organization={organization} />;
      case 'reservations':
        return <StockReservations organization={organization} />;
      case 'priceLists':
        return <ProductPriceLists organization={organization} />;
      case 'suppliers':
        return <ProductSuppliers organization={organization} />;
      case 'valuation':
        return <InventoryValuation organization={organization} />;
      case 'orders':
        return <ProductRelatedOrders organization={organization} />;
      case 'transfers':
        return <ProductRelatedTransfers organization={organization} />;
      case 'configuration':
        return <ProductConfiguration organization={organization} />;
      case 'tracking':
        return <ProductTracking organization={organization} />;
      default:
        return <ProductOverview organization={organization} />;
    }
  };

  // Show message if no organization is available
  if (!organization) {
    return (
      <div className="container product-management-main">
        <div className="page-header">
          <h1>{t('inventory.product_management_title')}</h1>
          <p className="page-subtitle">{t('inventory.product_management_subtitle')}</p>
        </div>
        <article>
          <p>{t('inventory.no_organization_warning')}</p>
        </article>
      </div>
    );
  }

  return (
    <div className="product-management-main">
      <div className="page-header">
        <h2 title={t('inventory.product_management_subtitle')}>{t('inventory.product_management_title')}</h2>
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          {t('inventory.overview_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          {t('inventory.products_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'variants' ? 'active' : ''}`}
          onClick={() => setActiveTab('variants')}
        >
          {t('inventory.variants_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'priceLists' ? 'active' : ''}`}
          onClick={() => setActiveTab('priceLists')}
        >
          {t('inventory.price_lists_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'suppliers' ? 'active' : ''}`}
          onClick={() => setActiveTab('suppliers')}
        >
          {t('inventory.suppliers_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'stock' ? 'active' : ''}`}
          onClick={() => setActiveTab('stock')}
        >
          {t('inventory.stock_levels_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'movements' ? 'active' : ''}`}
          onClick={() => setActiveTab('movements')}
        >
          {t('inventory.movements_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'reservations' ? 'active' : ''}`}
          onClick={() => setActiveTab('reservations')}
        >
          {t('inventory.reservations_tab')}
        </button>

        <button
          className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          {t('inventory.categories_tab')}
        </button>

        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          {t('inventory.orders_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'transfers' ? 'active' : ''}`}
          onClick={() => setActiveTab('transfers')}
        >
          {t('inventory.transfers_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'valuation' ? 'active' : ''}`}
          onClick={() => setActiveTab('valuation')}
        >
          {t('inventory.valuation_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'configuration' ? 'active' : ''}`}
          onClick={() => setActiveTab('configuration')}
        >
          {t('inventory.config_tab')}
        </button>
        <button
          className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracking')}
        >
          {t('inventory.tracking_tab')}
        </button>
      </div>

      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ProductManagement;